// Problem-code → operator copy for the floor screens and the needs-attention
// tray (spec 06 §5's stable UPPER_SNAKE catalog).
//
// This REUSES the office area's translated-code table (`TRANSLATED_PROBLEM_CODES`)
// rather than starting a second one: the codes are the module's, not a
// screen's, and two lists would drift the moment a slice adds a code to one of
// them. What is floor-specific is only the SHAPE of the input — the tray holds
// a persisted `OutboxFailure` (a plain record that survived a reload), not a
// live `RequestError` object — so the same resolution order is applied to that.
//
// Resolution order, identical to the office rule: a translated code, then the
// server's own problem title (already human text), then the caller's fallback.
// A raw UPPER_SNAKE code must never reach an operator, and neither must a
// generic "something went wrong" that hides a perfectly clear server sentence.

import type { TFunction } from "i18next";
import { TRANSLATED_PROBLEM_CODES } from "../../office/inventory/lib/errors";
import { formatMeasure } from "../../office/inventory/lib/measure";
import { codePointList, glyphCharactersSentence } from "../../office/sales/lib/documents";
import { RequestError } from "../../../lib/request";
import type { OutboxFailure } from "./floor-outbox";

/**
 * Module 09's shipment refusals that carry a SENTENCE of their own (spec 09
 * §4.8 / §9.10, P3-D PR-D3, D-286): the eight the pick screen renders as its
 * red card — `reserved_for_other`, `not_reserved`, `location_kind`, `no_lot`,
 * `wrong_product` (ruling 30), the unit's remaining, the line's remaining and
 * an already-picked unit. They are resolved here, by code AND by
 * `extensions.reason`, so the tray shows the SAME words for a pick that parked
 * out of a dead zone as the screen showed for one refused live.
 * Since PR-D4 (D-288) a ninth: DOCUMENT_GLYPH_COVERAGE, which the delivery
 * note's draw can meet.
 * Every other module-09 code (SHIPMENT_NOT_PICKING, ORDER_NOT_SHIPPABLE,
 * CREDIT_BLOCKED, …) has no floor sentence of its own and falls through to the
 * server's own problem title, exactly as the resolution order below says.
 *
 * The keys live in the SALES dictionary's floor block (`sales.floor.ship.*`,
 * areas/office/sales/sales.i18n.ts — WEB-OFFICE's file; this one only reads
 * them), so the words belong to the module whose document they are about.
 * `undefined` = "this code has no floor sentence; use the general rule".
 *
 * RENDERING means the DERIVED form (the `floorFailureDetail` rule below): the
 * 422's `remaining` is the wire's canonical text — the LINE's `numeric(14,3)`
 * or the UNIT's `numeric(18,6)`, which `remaining_of` distinguishes — and an
 * operator reads "1200 m", never "1200.000000 m".
 */
function shipFailureSentence(
  t: TFunction,
  failure: OutboxFailure,
  locale: string | undefined,
): string | undefined {
  const ext = failure.extensions;
  switch (failure.code) {
    case "UNIT_NOT_RESERVED": {
      const ref = ext["reserved_for_ref"];
      if (ext["reason"] === "reserved_for_other" && ref !== undefined && ref.length > 0) {
        return t("sales.floor.ship.reservedForOther", { ref });
      }
      return t("sales.floor.ship.notReserved");
    }
    case "UNIT_NOT_SHIPPABLE": {
      // "Put it away first" is the remedy ONLY for a unit still in receiving /
      // quarantine / in transit (ruling 8's `location_kind`). A held lot, a
      // unit in the wrong status or at another facility has no one-line
      // remedy the floor can offer, so the server's title stands.
      const kind = ext["location_kind"];
      if (ext["reason"] === "location_kind" && kind !== undefined && kind.length > 0) {
        return t("sales.floor.ship.notShippableLocation", { kind });
      }
      // `no_lot` — the seam's refusal of a LOTLESS unit, raised before any
      // movement is posted: 0008's `stock_units.lot_id` is nullable and 0063's
      // `shipment_lines.lot_id` is NOT NULL, because the recall trace joins the
      // shipped unit to the lot it came from. This reason DOES have a one-line
      // floor remedy and it is not "put it away" — the unit has to be received
      // against a lot — so it gets its own sentence rather than falling through
      // to a server title that would send the storekeeper to the wrong shelf.
      if (ext["reason"] === "no_lot") return t("sales.floor.ship.notShippableNoLot");
      // `wrong_product` (D-286 ruling 30) — the scanned unit holds a different
      // product from the line's. This used to arrive as `unit_status` with
      // `unit_status: "filled"`, which told the storekeeper a healthy drum was
      // in a bad state and sent them to check the drum. The remedy is the next
      // scan, so the sentence names it.
      if (ext["reason"] === "wrong_product") {
        return t("sales.floor.ship.notShippableWrongProduct");
      }
      return undefined;
    }
    case "QTY_EXCEEDS_REMAINING": {
      const remaining = ext["remaining"];
      if (remaining === undefined) return undefined;
      const derived = formatMeasure(remaining, { locale });
      if (ext["remaining_of"] === "unit") {
        return t("sales.floor.ship.exceedsUnit", {
          remaining: derived,
          uom: ext["measure_uom"] ?? "",
        }).replace(/\s{2,}/gu, " ");
      }
      // The LINE's frame: `uom_code` rides the extensions when the server
      // names it; a sentence without the noun is still the right number.
      return t("sales.floor.ship.exceedsLine", {
        remaining: derived,
        uom: ext["uom_code"] ?? "",
      }).replace(/\s{2,}/gu, " ");
    }
    case "UNIT_ALREADY_PICKED":
      return t("sales.floor.ship.alreadyPicked");
    // The delivery note's re-render (P3-D PR-D4, D-288 ruling 11): the note
    // holds characters the document fonts cannot print. Drawing it again
    // cannot fix that, so the sentence names the characters with their U+
    // codes (`code_points`, kept by `describeFailure`) and where they live —
    // the order's names, or the document header or footer in the sales
    // settings — which the office corrects. The office's re-render reads the
    // same two sentences.
    case "DOCUMENT_GLYPH_COVERAGE":
      return `${glyphCharactersSentence(t, codePointList(ext["code_points"]))} ${t(
        "sales.shipments.deliveryNoteGlyphs",
      )}`;
    default:
      return undefined;
  }
}

/**
 * The sentence to show for a failed floor mutation.
 *
 * Registered in `tools/ci/i18n-parity.ts`'s `KEY_TAKING_HELPERS` (argument 2 is
 * a translation key), so a typo in a fallback key fails the gate exactly like a
 * typo in a `t()` call.
 *
 * `locale` is the caller's `i18n.language`, read only by the sentences that
 * print a quantity (the `floorFailureDetail` rule); a caller that omits it
 * gets the runtime's default grouping, never a blank.
 */
export function floorFailureMessage(
  t: TFunction,
  failure: OutboxFailure,
  fallbackKey = "inventory.errors.generic",
  locale?: string,
): string {
  const code = failure.code;
  const ship = shipFailureSentence(t, failure, locale);
  if (ship !== undefined) return ship;
  if (code !== undefined && TRANSLATED_PROBLEM_CODES.has(code)) {
    return t(`inventory.errors.${code}`, { defaultValue: failure.message });
  }
  if (failure.httpStatus === undefined) return t("inventory.floor.tray.networkFailure");
  if (failure.httpStatus === 403) return t("inventory.floor.tray.forbidden");
  if (failure.httpStatus === 404) return t("inventory.floor.tray.notFound");
  // `message` is the SERVER's problem title and nothing else: `describeFailure`
  // deliberately does not fall back to `RequestError.message`, which would be
  // `unwrap`'s internal label ("cut", "count-record") whenever a proxy returns
  // an HTML 5xx — printing that token to an operator as the reason their work
  // failed. An empty message therefore means "no problem document arrived", and
  // the translated generic is the only honest thing left to say.
  if (failure.message.length > 0) return failure.message;
  return t(fallbackKey);
}

/**
 * The extra sentence a §9.1 conflict carries. `INSUFFICIENT_REMAINING` ships the
 * FRESH `remaining_measure` (and `serial_code`) precisely so the floor can
 * re-plan instead of retrying blind; rendering the number is the whole point of
 * the extension existing.
 *
 * RENDERING means the DERIVED form. The extension value is the wire's canonical
 * `numeric(18,6)` string ("1543.000000"), and it stays that way in the persisted
 * failure record — but this function builds an operator sentence, and every
 * other surface that puts this same number in front of an operator (the office
 * CutPanel, floor-unit's live re-plan line) routes it through the shared
 * `formatMeasure` first. Interpolating the raw wire here left one conflict
 * reading "1543" live and "1543.000000" once parked — the my-shift `420.000`
 * defect class. `locale` is the caller's `i18n.language`, same as floor-unit;
 * an unparseable extension is echoed verbatim by `formatMeasure` (honest beats
 * blank).
 */
export function floorFailureDetail(
  t: TFunction,
  failure: OutboxFailure,
  locale?: string,
): string | undefined {
  const remaining = failure.extensions["remaining_measure"];
  if (
    (failure.code === "INSUFFICIENT_REMAINING" || failure.code === "CUT_EXCEEDS_REMAINING") &&
    remaining !== undefined
  ) {
    return t("inventory.floor.tray.freshRemaining", {
      remaining: formatMeasure(remaining, { locale }),
      serial: failure.extensions["serial_code"] ?? "",
    });
  }
  const available = failure.extensions["available_qty_base"];
  if (failure.code === "INSUFFICIENT_ATP" && available !== undefined) {
    return t("inventory.floor.tray.availableQty", {
      available: formatMeasure(available, { locale }),
    });
  }
  return undefined;
}

/**
 * The support code for a failed floor READ. `useCachedRead` types its `error`
 * as `unknown` (it is TanStack's), so every screen that renders a `FloorError`
 * off a read narrows it here rather than each doing its own instanceof — the §4
 * preamble requires "error state with retry + support code" on every list, and
 * a code the operator can read out is the whole point of the request id.
 */
export function floorRequestId(error: unknown): string | undefined {
  return error instanceof RequestError ? error.requestId : undefined;
}

/**
 * A failed floor READ that the operator's ROLE caused, not the network.
 *
 * The distinction is the difference between two opposite affordances. Every
 * floor list renders a failed read as "could not load this — Retry", which is
 * right for a dead AP and wrong for a 403: the role will not hold the code a
 * minute from now, so the button invites an operator to stand at the bench
 * tapping Retry at a refusal. The floor's own 403 sentence
 * (`inventory.floor.tray.forbidden` — the one `floorFailureMessage` already
 * gives a refused MUTATION) belongs to the refused READ too, with no retry
 * beside it.
 *
 * A floor route is reachable by URL — a bookmark, a QR on a laminated card, a
 * tablet handed over mid-shift — so a screen whose tile is correctly absent
 * (D-118) still has to answer honestly when somebody arrives anyway.
 */
export function floorForbidden(error: unknown): boolean {
  return error instanceof RequestError && error.httpStatus === 403;
}
